import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";
import { goEntitiesApi } from "@/services/go-entities.service";
import type { GoMaterialItem, GoMaterialKindMetaItem } from "@/types/go-entity";
import type { File as ApiFile } from "@/types/api-v2";
import { cn } from "@/lib/utils";

const UPLOAD_KIND_NONE = "__none__";

type Props = {
  ginfObjId: number;
  items: GoMaterialItem[];
  isLoading?: boolean;
  addPending?: boolean;
  deletePending?: boolean;
  onUploadFile: (file: File, materialKind: string | null) => void;
  onDownload: (item: GoMaterialItem) => void;
  onDelete: (item: GoMaterialItem) => void;
};

function isImage(item: GoMaterialItem): boolean {
  const ct = (item.content_type || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const name = (item.name || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|svg)$/.test(name);
}

function toPreviewFile(item: GoMaterialItem, ginfObjId: number): ApiFile {
  const name = item.name?.trim() || `material-${item.ginf_file_id}`;
  const mimetype =
    item.content_type?.trim() ||
    (/\.png$/i.test(name)
      ? "image/png"
      : /\.jpe?g$/i.test(name)
        ? "image/jpeg"
        : /\.webp$/i.test(name)
          ? "image/webp"
          : /\.gif$/i.test(name)
            ? "image/gif"
            : /\.svg$/i.test(name)
              ? "image/svg+xml"
              : "application/octet-stream");
  return {
    id: item.ginf_file_id,
    object_id: ginfObjId,
    name,
    path: "",
    mimetype,
    size: item.size ?? 0,
    created_at: new Date(0).toISOString(),
  };
}

function MaterialThumb({
  ginfObjId,
  item,
  onOpen,
}: {
  ginfObjId: number;
  item: GoMaterialItem;
  onOpen?: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!isImage(item)) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const blob = await goEntitiesApi.downloadMaterialBlob(ginfObjId, item.ginf_file_id);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ginfObjId, item.ginf_file_id, item.content_type, item.name]);

  if (!isImage(item)) {
    return (
      <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded border border-dashed border-border bg-muted/30 text-[10px] text-muted-foreground">
        файл
      </div>
    );
  }
  if (!url) {
    return <Skeleton className="h-14 w-20 shrink-0 rounded" />;
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "h-14 w-20 shrink-0 overflow-hidden rounded border border-border bg-muted/20 p-0",
        "hover:ring-2 hover:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "cursor-zoom-in",
      )}
      title="Открыть просмотр"
      aria-label={`Просмотр: ${item.name || item.ginf_file_id}`}
    >
      <img src={url} alt={item.name || ""} className="h-full w-full object-cover" />
    </button>
  );
}

export function CountryMaterialsGallery({
  ginfObjId,
  items,
  isLoading,
  addPending,
  deletePending,
  onUploadFile,
  onDownload,
  onDelete,
}: Props) {
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [uploadKind, setUploadKind] = useState<string>("");
  const [kindMeta, setKindMeta] = useState<GoMaterialKindMetaItem[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<ApiFile[]>([]);
  const [previewInitialIndex, setPreviewInitialIndex] = useState(0);

  const fetchBlob = useCallback(
    (fileId: number) => goEntitiesApi.downloadMaterialBlob(ginfObjId, fileId),
    [ginfObjId],
  );

  useEffect(() => {
    let cancelled = false;
    goEntitiesApi
      .listMaterialKinds()
      .then((rows) => {
        if (!cancelled) setKindMeta(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setKindMeta([
            { code: "flag", label: "Флаг" },
            { code: "coat", label: "Герб" },
            { code: "symbols", label: "Национальные символы" },
            { code: "raster", label: "Растровая карта" },
          ]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const labelByCode = useMemo(() => {
    const m = new Map(kindMeta.map((k) => [k.code, k.label]));
    return m;
  }, [kindMeta]);

  const filtered = useMemo(() => {
    if (kindFilter === "all") return items;
    if (kindFilter === "unset") return items.filter((i) => !i.material_kind);
    return items.filter((i) => i.material_kind === kindFilter);
  }, [items, kindFilter]);

  const imageItems = useMemo(() => items.filter(isImage), [items]);

  const openPreview = useCallback(
    (item: GoMaterialItem) => {
      if (!isImage(item)) return;
      const images = imageItems.length > 0 ? imageItems : [item];
      const files = images.map((m) => toPreviewFile(m, ginfObjId));
      const idx = Math.max(
        0,
        images.findIndex((m) => m.ginf_file_id === item.ginf_file_id),
      );
      setPreviewFiles(files);
      setPreviewInitialIndex(idx);
      setPreviewOpen(true);
    },
    [ginfObjId, imageItems],
  );

  const grouped = useMemo(() => {
    const order = [...kindMeta.map((k) => k.code), ""];
    const buckets = new Map<string, GoMaterialItem[]>();
    for (const item of filtered) {
      const key = item.material_kind || "";
      const list = buckets.get(key) ?? [];
      list.push(item);
      buckets.set(key, list);
    }
    return order
      .filter((k) => buckets.has(k))
      .map((k) => ({
        kind: k,
        label: k ? labelByCode.get(k) || k : "Без вида",
        items: buckets.get(k)!,
      }));
  }, [filtered, kindMeta, labelByCode]);

  return (
    <div className="space-y-3 text-foreground">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1.5 text-sm font-medium">
          Материалы страны
          <SectionHelpTooltip label="Справка: материалы Country Hub">
            <p className="font-medium">Зачем</p>
            <p>
              Флаги, гербы, сводные символы и тематические растры пакета — с превью и фильтром по
              виду.
            </p>
            <p className="mt-2 font-medium">Как работать</p>
            <ul className="list-disc pl-4">
              <li>Клик по превью — просмотр в диалоге (как материалы ОИ), стрелки листают соседние.</li>
              <li>Фильтр сверху — по виду (flag / coat / symbols / raster).</li>
              <li>Перед загрузкой выберите вид; для flag/coat/symbols повторная загрузка заменяет.</li>
              <li>Скачать / Удалить — справа у строки. Esc закрывает просмотр.</li>
            </ul>
          </SectionHelpTooltip>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Button
          type="button"
          size="sm"
          variant={kindFilter === "all" ? "default" : "outline"}
          onClick={() => setKindFilter("all")}
        >
          Все
        </Button>
        {kindMeta.map((k) => (
          <Button
            key={k.code}
            type="button"
            size="sm"
            variant={kindFilter === k.code ? "default" : "outline"}
            onClick={() => setKindFilter(k.code)}
          >
            {k.label}
          </Button>
        ))}
        <Button
          type="button"
          size="sm"
          variant={kindFilter === "unset" ? "default" : "outline"}
          onClick={() => setKindFilter("unset")}
        >
          Без вида
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={uploadKind || UPLOAD_KIND_NONE}
          onValueChange={(v) => setUploadKind(v === UPLOAD_KIND_NONE ? "" : v)}
        >
          <SelectTrigger className="w-[220px] bg-background" aria-label="Вид при загрузке">
            <SelectValue placeholder="Вид при загрузке" />
          </SelectTrigger>
          <SelectContent className="z-[250]" position="popper">
            <SelectItem value={UPLOAD_KIND_NONE}>Без вида</SelectItem>
            {kindMeta.map((k) => (
              <SelectItem key={k.code} value={k.code}>
                {k.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="file"
          accept="image/*,.png,.jpg,.jpeg,.webp"
          disabled={addPending}
          className="max-w-xs"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUploadFile(file, uploadKind || null);
            e.target.value = "";
          }}
        />
        {addPending ? (
          <span className="text-xs text-muted-foreground shrink-0">Загрузка…</span>
        ) : null}
      </div>

      <div className="border rounded-md">
        {isLoading ? (
          <div className="p-3 space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-3 text-sm text-muted-foreground">Материалы не добавлены</div>
        ) : (
          grouped.map((group) => (
            <div key={group.kind || "unset"} className="border-b last:border-b-0">
              <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground bg-muted/30">
                {group.label}
              </div>
              {group.items.map((item) => (
                <div
                  key={item.ginf_file_id}
                  className={cn(
                    "grid grid-cols-[auto_1fr_auto] gap-3 px-3 py-2 border-b last:border-b-0 text-sm items-center",
                  )}
                >
                  <MaterialThumb
                    ginfObjId={ginfObjId}
                    item={item}
                    onOpen={isImage(item) ? () => openPreview(item) : undefined}
                  />
                  <div className="min-w-0">
                    <button
                      type="button"
                      className={cn(
                        "block max-w-full truncate text-left font-medium",
                        isImage(item) && "hover:underline cursor-pointer",
                      )}
                      onClick={() => {
                        if (isImage(item)) openPreview(item);
                      }}
                      disabled={!isImage(item)}
                    >
                      {item.name?.trim() || `Материал #${item.ginf_file_id}`}
                    </button>
                    <div className="text-xs text-muted-foreground">
                      {item.size != null ? `${item.size} байт` : "—"}
                      {item.content_type ? ` · ${item.content_type}` : ""}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {isImage(item) ? (
                      <Button size="sm" variant="outline" onClick={() => openPreview(item)}>
                        Просмотр
                      </Button>
                    ) : null}
                    <Button size="sm" variant="outline" onClick={() => onDownload(item)}>
                      Скачать
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={deletePending}
                      onClick={() => onDelete(item)}
                    >
                      Удалить
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <FilePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        fetchBlob={fetchBlob}
        files={previewFiles}
        initialIndex={previewInitialIndex}
      />
    </div>
  );
}
